import { useEffect, useState } from 'react';
import { Loader2, Star, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StarRatingInput } from '@/components/ui/star-rating';
import { form_error_message } from '@/lib/forms';
import { use_create_review, use_update_review, use_delete_review } from '@/features/review/hooks';
import type { OwnerReview } from '@/features/review/types';

const MAX = 1000;

export function ReviewModal({
  booking_id,
  existing,
  title,
  on_close,
}: {
  booking_id: string;
  existing: OwnerReview | null;
  title?: string;
  on_close: () => void;
}) {
  const [rating, set_rating] = useState(existing?.rating ?? 0);
  const [comment, set_comment] = useState(existing?.comment ?? '');
  const create_m = use_create_review();
  const update_m = use_update_review();
  const delete_m = use_delete_review();
  const busy = create_m.isPending || update_m.isPending || delete_m.isPending;
  const error = create_m.error ?? update_m.error ?? delete_m.error;

  useEffect(() => {
    const on_key = (e: KeyboardEvent) => e.key === 'Escape' && !busy && on_close();
    window.addEventListener('keydown', on_key);
    return () => window.removeEventListener('keydown', on_key);
  }, [on_close, busy]);

  async function submit() {
    if (rating < 1) return;
    const input = { rating, comment: comment.trim() || undefined };
    if (existing) await update_m.mutateAsync({ booking_id, input });
    else await create_m.mutateAsync({ booking_id, input });
    on_close();
  }

  async function remove() {
    if (!window.confirm('Delete your review?')) return;
    await delete_m.mutateAsync(booking_id);
    on_close();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="review-modal"
      onClick={() => !busy && on_close()}
    >
      <div
        className="my-8 w-full max-w-md rounded-card border-2 border-brand-outline-variant/20 bg-card shadow-clay-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold">
            <Star className="h-5 w-5 fill-amber-400 text-amber-400" />
            {existing ? 'Edit your review' : 'Rate your visit'}
          </h2>
          <button type="button" onClick={on_close} aria-label="Close" className="rounded-full p-2 hover:bg-muted" data-testid="review-modal-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col gap-4 p-5">
          {title && <p className="text-sm text-muted-foreground">{title}</p>}

          <div className="flex flex-col items-center gap-2 py-2">
            <StarRatingInput value={rating} onChange={set_rating} disabled={busy} testid="review-stars" />
            <span className="text-xs text-muted-foreground">{rating ? `${rating} of 5` : 'Tap a star to rate'}</span>
          </div>

          <div>
            <label htmlFor="review-comment" className="mb-1 block text-sm font-semibold">
              Comment <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <textarea
              id="review-comment"
              data-testid="review-comment"
              value={comment}
              maxLength={MAX}
              onChange={(e) => set_comment(e.target.value)}
              rows={4}
              placeholder="Share how the visit went…"
              className="w-full resize-none rounded-input border-2 border-input bg-background p-3 text-sm outline-none transition-colors focus:border-primary/60"
            />
            <div className="mt-1 text-right text-xs text-muted-foreground">{comment.length}/{MAX}</div>
          </div>

          {error && (
            <p className="text-sm text-destructive" data-testid="review-error">
              {form_error_message(error, 'Could not save your review.')}
            </p>
          )}

          <div className="flex items-center gap-2">
            <Button variant="gradient" className="flex-1" disabled={rating < 1 || busy} onClick={submit} data-testid="review-submit">
              {busy && !delete_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : existing ? 'Save changes' : 'Submit review'}
            </Button>
            {existing && (
              <Button variant="ghost" size="icon" disabled={busy} onClick={remove} aria-label="Delete review" data-testid="review-delete">
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
